import { z } from 'zod';
/**
 * Контракты эндпоинтов `modules/views` (docs/api-design.md §4.6,
 * docs/data-model.md §3.6, docs/REQUIREMENTS.md FR-5).
 *
 * Multi-type views: представление привязано к 1+ типам записей (itemTypeIds).
 */
// ─── Filter / Sort / Grouping ─────────────────────────────────────────────────
export const FilterLeafSchema = z.object({
    fieldSlug: z.string(),
    operator: z.string(),
    value: z.unknown().optional(),
    /**
     * Связка с предыдущим условием в массиве. Опциональный — для первого
     * условия игнорируется. Семантика: SQL-precedence (AND > OR), т.е.
     * `[a, b, c(or), d]` → `(a AND b) OR (c AND d)`.
     */
    combinator: z.enum(['and', 'or']).optional(),
});
/**
 * Значение-подстановка «текущий пользователь» в условии фильтра. Хранится в
 * `FilterLeaf.value` вместо конкретного id и раскрывается на бэкенде в момент
 * запроса — у каждого пользователя в своё значение:
 *
 * - поле типа `user` (и мета-поле `_created_by`) → id самого пользователя;
 * - поле типа `reference` → запись справочника, у которой значение ключевой
 *   колонки совпадает с логином (затем email) пользователя — то же соответствие,
 *   что у автоподстановки `config.autofillCurrentUser` и Jira-синка.
 *
 * Нужно для общих на всё представление быстрых фильтров вроде «Мои задачи»:
 * один и тот же фильтр каждому показывает его собственные записи.
 */
export const CURRENT_USER_TOKEN = '@me';
/** Значение условия — подстановка «текущий пользователь». */
export function isCurrentUserToken(value) {
    return value === CURRENT_USER_TOKEN;
}
/** В условии (одиночное значение или список) есть подстановка «текущий пользователь». */
export function leafUsesCurrentUser(leaf) {
    const v = leaf.value;
    return Array.isArray(v) ? v.some(isCurrentUserToken) : isCurrentUserToken(v);
}
/** Хотя бы одно условие набора использует подстановку «текущий пользователь». */
export function filtersUseCurrentUser(filters) {
    return filters.some(leafUsesCurrentUser);
}
export const SortEntrySchema = z.object({
    fieldSlug: z.string(),
    direction: z.enum(['asc', 'desc']),
});
export const GroupingSchema = z.object({
    fieldSlug: z.string(),
    order: z.enum(['name', 'count']).default('name'),
    defaultExpanded: z.boolean().default(true),
});
/**
 * Быстрый фильтр (quick filter) — именованный пресет условий, общий для всех
 * пользователей в рамках представления (хранится в `view.config.quickFilters`).
 * По аналогии с quick filters в Structure для Jira: над гридом рисуются чипы-
 * переключатели, пользователь включает любой набор, активные комбинируются по AND
 * (запись должна пройти каждый включённый фильтр). Внутри одного быстрого фильтра —
 * собственная логика И/ИЛИ (тот же плоский `FilterLeaf[]` с precedence AND > OR).
 *
 * Редактировать список может только admin (через PATCH view). Какие фильтры сейчас
 * включены — это личное per-view состояние клиента, а не часть config.
 */
export const QuickFilterSchema = z.object({
    /** Стабильный идентификатор (генерируется на клиенте, напр. crypto.randomUUID). */
    id: z.string().min(1),
    name: z.string().trim().min(1).max(100),
    /** Опциональный цвет чипа (например, hex или AntD preset), украшение. */
    color: z.string().max(32).optional(),
    filters: z.array(FilterLeafSchema).default([]),
});
/**
 * Колонка-поле: показывает значение `fields[fieldSlug]` записи. По умолчанию
 * (когда `kind` не указан) колонка считается полевой — для обратной совместимости
 * со старыми view.config'ами, где `{fieldSlug, width, pinned}` хранился без
 * дискриминатора.
 */
/**
 * Агрегат, показываемый в строке итогов под числовой колонкой представления.
 * Считается целиком на клиенте по видимым (отфильтрованным) записям — отдельного
 * API не требуется. Отсутствие поля = итог по колонке не показывается.
 */
export const ColumnAggregateSchema = z.enum(['sum', 'avg', 'min', 'max']);
export const FieldColumnConfigSchema = z.object({
    kind: z.literal('field').optional(),
    fieldSlug: z.string(),
    width: z.number().optional(),
    pinned: z.enum(['left', 'right']).optional(),
    /** Итог по числовой колонке в подвале таблицы (sum/avg/min/max). */
    aggregate: ColumnAggregateSchema.optional(),
});
/**
 * Колонка «связанные записи»: бэкенд считает агрегат по `item_links` для
 * каждой записи текущей страницы (см. M11.6 в `docs/ROADMAP.md`).
 *
 * - `linkRuleIds` — фильтрует связи по набору правил (учитываются связи,
 *   созданные любым из перечисленных правил). Пустой массив или отсутствие
 *   поля = все связи записи. Поле `linkRuleId` (одиночное) сохранено для
 *   обратной совместимости со старыми view.config'ами и при чтении
 *   нормализуется как `[linkRuleId]`.
 * - `linkedItemTypeIds` — новый основной фильтр: берём текущие item_links
 *   записи и оставляем только связанные объекты выбранных типов.
 * - `linkedFilters` — дополнительные фильтры по полям связанных объектов.
 * - `linkType` — фильтрует по Jira link-type (`Blocks`, `Relates`, …).
 * - `mode`:
 *     - `list`  — рендерится список заголовков связанных записей;
 *     - `count` — количество связей;
 *     - `sum` / `avg` — агрегат по числовому полю target-типа,
 *       заданному `valueFieldSlug` (обязательно).
 *
 * Ключ колонки в response — стабильный `_link:{key}` (key вычисляется
 * детерминированно из linkRuleId + linkType + mode + valueFieldSlug).
 */
export const LinkAggregateColumnConfigSchema = z
    .object({
    kind: z.literal('link_aggregate'),
    /** Стабильный ключ колонки внутри view.config (нужен для localStorage-настроек ширины/скрытия). */
    key: z.string(),
    /** Заголовок, показываемый в шапке таблицы. */
    label: z.string(),
    linkRuleId: z.string().uuid().optional(),
    linkRuleIds: z.array(z.string().uuid()).max(50).optional(),
    linkedItemTypeId: z.string().uuid().optional(),
    linkedItemTypeIds: z.array(z.string().uuid()).max(50).optional(),
    linkedFilters: z.array(FilterLeafSchema).max(20).optional(),
    linkType: z.string().optional(),
    mode: z.enum(['list', 'count', 'sum', 'avg']),
    valueFieldSlug: z.string().optional(),
    width: z.number().optional(),
    pinned: z.enum(['left', 'right']).optional(),
})
    .refine((cfg) => cfg.mode === 'list' || cfg.mode === 'count' || !!cfg.valueFieldSlug, { message: 'valueFieldSlug обязателен для mode=sum|avg', path: ['valueFieldSlug'] });
export const ViewComputedColumnConfigSchema = z
    .object({
    kind: z.literal('view_computed'),
    key: z.string().min(1),
    label: z.string().trim().min(1).max(200),
    formula: z.string().trim().min(1).max(2000),
    returnType: z.enum(['number', 'text']).default('number'),
    numberDecimals: z.number().int().min(0).max(6).optional(),
    width: z.number().optional(),
    pinned: z.enum(['left', 'right']).optional(),
})
    .refine((cfg) => cfg.returnType === 'number' || cfg.numberDecimals === undefined, { message: 'numberDecimals допустим только для returnType=number', path: ['numberDecimals'] });
/**
 * Колонка «атрибут справочника по reference-полю». Берёт значение reference-поля
 * текущей записи (UUID DictionaryEntry'а), и показывает в этой колонке значение
 * по цепочке колонок справочников. Резолвится полностью на клиенте — никаких
 * отдельных API или join-ов на бэкенде не требуется, т.к. клиент уже грузит
 * entries через ensureReferenceOptions/dictionaryOptionsById.
 *
 * `path` — последовательность slug'ов колонок справочников, начиная с
 * справочника, на который смотрит referenceField. Если на каком-то шаге колонка
 * имеет тип `reference`, следующий элемент path относится к колонкам её
 * целевого справочника, и так далее. Длина 1..5 (последний элемент — лист).
 *
 * Пример: цель → reference `Ответственный` (справочник «Сотрудники»),
 * `path = ['department', 'head', 'full_name']` — где `department` ссылается
 * на «Отделы», у которого `head` ссылается обратно на «Сотрудники», и
 * `full_name` — текстовая колонка.
 *
 * Backward-compat: legacy-конфиги с `attributeSlug` без `path` нормализуются
 * в `path: [attributeSlug]`.
 */
export const REFERENCE_ATTRIBUTE_MAX_DEPTH = 5;
export const ReferenceAttributeColumnConfigSchema = z.preprocess((raw) => {
    if (!raw || typeof raw !== 'object')
        return raw;
    const obj = raw;
    if (obj.kind !== 'reference_attribute')
        return raw;
    if (Array.isArray(obj.path) && obj.path.length > 0)
        return raw;
    if (typeof obj.attributeSlug === 'string' && obj.attributeSlug.length > 0) {
        return { ...obj, path: [obj.attributeSlug] };
    }
    return raw;
}, z.object({
    kind: z.literal('reference_attribute'),
    key: z.string().min(1),
    label: z.string().trim().min(1).max(200),
    /** Slug reference-поля в текущем item type (или одного из item type'ов multi-type view). */
    referenceFieldSlug: z.string().min(1),
    /** Цепочка slug'ов колонок справочников (1..REFERENCE_ATTRIBUTE_MAX_DEPTH). */
    path: z.array(z.string().min(1)).min(1).max(REFERENCE_ATTRIBUTE_MAX_DEPTH),
    /** Legacy (deprecated): первый элемент path. Сохраняется для совместимости при сериализации. */
    attributeSlug: z.string().min(1).optional(),
    width: z.number().optional(),
    pinned: z.enum(['left', 'right']).optional(),
}));
/**
 * Объединение всех вариантов колонки. Для backward-compat сначала пробуем
 * synthetic-колонки по полю `kind`, затем валидируем как field-колонку.
 */
export const ColumnConfigSchema = z.union([
    LinkAggregateColumnConfigSchema,
    ViewComputedColumnConfigSchema,
    ReferenceAttributeColumnConfigSchema,
    FieldColumnConfigSchema,
]);
// ─── View config schemas ─────────────────────────────────────────────────────
export const TableViewConfigSchema = z.object({
    kind: z.literal('table'),
    columns: z.array(ColumnConfigSchema).default([]),
    filters: z.array(FilterLeafSchema).default([]),
    sorts: z.array(SortEntrySchema).default([]),
    /** Быстрые фильтры (пресеты-чипы), общие в рамках представления. */
    quickFilters: z.array(QuickFilterSchema).default([]),
    /** Multi-level grouping: array of grouping levels (first = top-level). */
    grouping: z.union([GroupingSchema, z.array(GroupingSchema)]).optional(),
    frozenColumns: z.number().optional(),
});
export const KanbanViewConfigSchema = z.object({
    kind: z.literal('kanban'),
    columnField: z.string(),
    /**
     * Опциональная цепочка slug'ов колонок справочников (1..REFERENCE_ATTRIBUTE_MAX_DEPTH),
     * аналогично `ReferenceAttributeColumnConfig.path`. Когда указана, группировка
     * канбана идёт не по самому reference-полю записи, а по значению на конце цепочки.
     * Drag-and-drop в этом режиме отключён, т.к. значение лежит в справочнике.
     */
    columnFieldPath: z
        .array(z.string().min(1))
        .min(1)
        .max(REFERENCE_ATTRIBUTE_MAX_DEPTH)
        .optional(),
    visibleColumnValues: z.array(z.string()).default([]),
    /**
     * Сохранённая сортировка карточек внутри колонок — общая для всех, кто
     * открывает доску (как `sorts` у таблицы). Когда задана, ручной порядок
     * карточек в колонке (drag-and-drop по слотам sort_order) не сохраняется.
     */
    sorts: z.array(SortEntrySchema).default([]),
    /**
     * Порядок колонок доски: значения опций статусного поля в том порядке, в каком
     * их показывать. Хранится вместе со скрытыми колонками, чтобы при включении
     * колонка вставала на своё место. Значения, которых тут нет (например, опция,
     * добавленная позже), идут после перечисленных в порядке опций поля.
     * Пусто = порядок опций поля.
     */
    columnOrder: z.array(z.string()).default([]),
    filters: z.array(FilterLeafSchema).default([]),
    /** Быстрые фильтры (пресеты-чипы), общие в рамках представления. */
    quickFilters: z.array(QuickFilterSchema).default([]),
    cardTitleField: z.string(),
    cardSubtitleField: z.string().optional(),
    cardExtraFields: z.array(z.string()).max(3).default([]),
});
export const ViewConfigSchema = z.discriminatedUnion('kind', [
    TableViewConfigSchema,
    KanbanViewConfigSchema,
]);
// ─── View DTO ────────────────────────────────────────────────────────────────
export const ViewSchema = z.object({
    id: z.string().uuid(),
    spaceId: z.string().uuid(),
    itemTypeIds: z.array(z.string().uuid()),
    name: z.string(),
    icon: z.string().nullable().optional(),
    viewType: z.enum(['table', 'kanban']),
    config: z.record(z.unknown()),
    isDefault: z.boolean(),
    isArchived: z.boolean(),
    group: z.string().nullable().optional(),
    sortOrder: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const ViewListResponseSchema = z.object({
    views: z.array(ViewSchema),
});
// ─── Create view ─────────────────────────────────────────────────────────────
export const CreateViewRequestSchema = z.object({
    itemTypeIds: z.array(z.string().uuid()).min(1),
    name: z.string().trim().min(1).max(200),
    icon: z.string().max(48).optional(),
    viewType: z.enum(['table', 'kanban']).default('table'),
    config: z.record(z.unknown()).optional().default({}),
    isDefault: z.boolean().optional().default(false),
    group: z.string().max(100).optional(),
});
// ─── Update view ─────────────────────────────────────────────────────────────
export const UpdateViewRequestSchema = z.object({
    name: z.string().trim().min(1).max(200).optional(),
    icon: z.string().max(48).nullable().optional(),
    itemTypeIds: z.array(z.string().uuid()).min(1).optional(),
    config: z.record(z.unknown()).optional(),
    isDefault: z.boolean().optional(),
    isArchived: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
    group: z.string().max(100).nullable().optional(),
});
// ─── Reorder views ───────────────────────────────────────────────────────────
export const ReorderViewsRequestSchema = z.object({
    orderedIds: z.array(z.string().uuid()).min(1),
});
// ─── Extended query (M6: views + filters) ────────────────────────────────────
export const QueryItemsWithViewRequestSchema = z.object({
    /** Required when no viewId; ignored when viewId resolves item types. */
    itemTypeIds: z.array(z.string().uuid()).optional(),
    viewId: z.string().uuid().optional(),
    additionalFilters: z.array(FilterLeafSchema).optional(),
    sorts: z.array(SortEntrySchema).optional(),
    search: z.string().optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().positive().max(500).optional().default(100),
});
